import React, { useEffect, useState } from 'react';
import DatePicker from "react-datepicker";
import Header from '../../layouts/Header';
import { toast } from 'react-toastify';
import axios from '../../api/axios';
import { Button, Col, Form, Row, Spinner, Card, Table, Badge } from 'react-bootstrap';
import { format } from 'date-fns';

const ItemWiseSummary = () => {
    const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
    const [skin, setSkin] = useState(currentSkin);
   
    const accessToken = localStorage.getItem('authToken');
    const [outlets, setOutlets] = useState([]);
    const [itemWiseData, setItemWiseData] = useState([]);
    const [selectedOutlet, setSelectedOutlet] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [subBtnLoading, setSubBtnLoading] = useState(false);
    const [outletName, setOutletName] = useState('');
    const [dateRangeLabel, setDateRangeLabel] = useState('');

    const getOutlets = async () => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };
            const response = await axios.get("outlet", { headers });
       
            if (response?.data?.status === true) {
                let filteredOutlets = response?.data?.data?.outlets?.filter((act) => act?.status === "ACTIVE");
                setOutlets(filteredOutlets);
            }
        } catch (error) {
            if (error.response && error.response?.data?.status === false) {
                let errorMessage = error.response.data.error;
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
            }
        }
    };
   
    const getItemWiseSummary = async (startDate, endDate, outletId) => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };
            
            let newUrl = `summary/day-item-wise-sales-summary?startDate=${startDate?.toISOString()?.split("T")[0]}&endDate=${endDate?.toISOString()?.split("T")[0]}`;
            
            if (outletId) {
                newUrl += `&outletId=${outletId}`;
                const selectedOutlet = outlets.find(outlet => outlet._id === outletId);
                setOutletName(selectedOutlet?.name || '');
            }
           
            const response = await axios.get(newUrl, { headers });
           
            if (response?.data?.status === true) {
                const itemsWithSerial = response?.data?.data?.reportData?.map((item, index) => ({
                    ...item,
                    serialNumber: index + 1,
                    // Calculate quantity based on actualTaxable and rate
                    quantity: item.actualTaxable && item.rate ? Math.round(item.actualTaxable / item.rate) : 0
                }));
                
                setItemWiseData(itemsWithSerial || []);
                
                // Set date range label
                if (startDate && endDate) {
                    const start = format(startDate, 'dd MMM yyyy');
                    const end = format(endDate, 'dd MMM yyyy');
                    setDateRangeLabel(start === end ? start : `${start} - ${end}`);
                }
            }
        } catch (error) {
            if (error.response && error.response?.data?.status === false) {
                let errorMessage = error.response.data.error;
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
            }
        }
    };

    const handleSubmit = async() => {
        if (!startDate || !endDate) {
            toast.error("Please select both start and end dates", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
            return;
        }
        
        if (startDate > endDate) {
            toast.error("Start date cannot be after end date", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
            return;
        }
        
        setSubBtnLoading(true);
        const adjustedStartDate = new Date(startDate);
        adjustedStartDate.setHours(0, 0, 0, 0);
        
        const adjustedEndDate = new Date(endDate);
        adjustedEndDate.setHours(23, 59, 59, 999);
        
        await getItemWiseSummary(adjustedStartDate, adjustedEndDate, selectedOutlet);
        setSubBtnLoading(false);
    }

    const formatCurrency = (value) => {
        if (value === null || value === undefined) return '₹0.00';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(value);
    };

    const getFoodTypeBadge = (type) => {
        const variants = {
            'VEG': 'success',
            'NON-VEG': 'danger',
            'VEGAN': 'info',
            'default': 'secondary'
        };
       
        return (
            <Badge bg={variants[type] || variants.default} className="text-capitalize">
                {type.toLowerCase()}
            </Badge>
        );
    };

    // Calculate totals for all items
    const calculateTotals = () => {
        return {
            totalItems: itemWiseData.length,
            totalQty: itemWiseData.reduce((sum, item) => sum + (item.quantity || 0), 0),
            totalTaxable: itemWiseData.reduce((sum, item) => sum + (item.totalTaxable || 0), 0),
            totalDiscount: itemWiseData.reduce((sum, item) => sum + (item.totalDiscountAmount || 0), 0),
            totalTax: itemWiseData.reduce((sum, item) => sum + (item.totalTaxAmount || 0), 0),
            grandTotal: itemWiseData.reduce((sum, item) => sum + (item.grandTotal || 0), 0),
            actualTaxable: itemWiseData.reduce((sum, item) => sum + (item.actualTaxable || 0), 0)
        };
    };

    // Group items by category
    const getCategoryStats = () => {
        const categories = {};
        
        itemWiseData?.forEach(item => {
            if (!categories[item.category]) {
                categories[item.category] = {
                    count: 0,
                    quantity: 0,
                    taxable: 0,
                    discount: 0,
                    tax: 0,
                    total: 0
                };
            }
            
            categories[item.category].count++;
            categories[item.category].quantity += item.quantity || 0;
            categories[item.category].taxable += item.totalTaxable || 0;
            categories[item.category].discount += item.totalDiscountAmount || 0;
            categories[item.category].tax += item.totalTaxAmount || 0;
            categories[item.category].total += item.grandTotal || 0;
        });
        
        return Object.entries(categories).map(([name, stats]) => ({
            name,
            ...stats,
            percentage: calculateTotals().grandTotal > 0 ? (stats.total / calculateTotals().grandTotal) * 100 : 0
        }));
    };

    // Group items by food type
    const getFoodTypeStats = () => {
        const types = {};
        
        itemWiseData.forEach(item => {
            if (!types[item.foodType]) {
                types[item.foodType] = {
                    count: 0,
                    quantity: 0,
                    taxable: 0,
                    discount: 0,
                    tax: 0,
                    total: 0
                };
            }
            
            types[item.foodType].count++;
            types[item.foodType].quantity += item.quantity || 0;
            types[item.foodType].taxable += item.totalTaxable || 0;
            types[item.foodType].discount += item.totalDiscountAmount || 0;
            types[item.foodType].tax += item.totalTaxAmount || 0;
            types[item.foodType].total += item.grandTotal || 0;
        });
        
        return Object.entries(types).map(([name, stats]) => ({
            name,
            ...stats,
            percentage: calculateTotals().grandTotal > 0 ? (stats.total / calculateTotals().grandTotal) * 100 : 0
        }));
    };

    useEffect(() => {
        getOutlets();
    }, []);

    useEffect(() => {
        const now = new Date();
        const currentDate = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate()
        );
        setStartDate(currentDate);
        setEndDate(currentDate);
    }, []);

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div
                className={`main main-app p-3 p-lg-4 ${
                skin === "dark" ? "bg-dark text-light" : "bg-light"
                }`}
            >
                <div className="d-flex flex-column gap-3">
                    <div className={`card ${skin === "dark" ? "bg-dark-dark" : ""} border-0 shadow-sm`}>
                        <div className="card-header bg-white border-0 py-3">
                            <h4 className="mb-0 fw-bold">
                                <i className="bi bi-bag me-2"></i>ITEM WISE SALES SUMMARY
                            </h4>
                        </div>
                        <hr className="mt-0 mb-0" />
             
                        <div className="card-body">
                            {/* Filters Section */}
                            <div className="filters-section p-3 bg-light rounded-3 mb-4">
                                <Row className="g-3">
                                    <Col md={3}>
                                        <Form.Group controlId="outletSelect">
                                            <Form.Label className="fw-semibold">
                                                Select Outlet <span className='text-danger'>*</span>
                                            </Form.Label>
                                            <Form.Select
                                                value={selectedOutlet}
                                                onChange={(e) => setSelectedOutlet(e.target.value)}
                                                className="border-secondary"
                                            >
                                            <option value="">All Outlets</option>
                                            {outlets.map((outlet, index) => (
                                                <option key={index} value={outlet?._id}>
                                                {outlet?.name}
                                                </option>
                                            ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col md={2}>
                                        <Form.Group controlId="startDatePicker">
                                            <Form.Label className="fw-semibold">
                                                Start Date <span className="text-danger">*</span>
                                            </Form.Label>
                                            <DatePicker
                                                selected={startDate}
                                                onChange={(date) => setStartDate(date)}
                                                dateFormat="dd-MM-yyyy"
                                                placeholderText="Select Start Date"
                                                className="form-control border-secondary"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={2}>
                                        <Form.Group controlId="endDatePicker">
                                            <Form.Label className="fw-semibold">
                                                End Date <span className="text-danger">*</span>
                                            </Form.Label>
                                            <DatePicker
                                                selected={endDate}
                                                onChange={(date) => setEndDate(date)}
                                                dateFormat="dd-MM-yyyy"
                                                placeholderText="Select End Date"
                                                className="form-control border-secondary"
                                                minDate={startDate}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={2} className="d-flex align-items-end">
                                        <Button
                                            variant="primary"
                                            className="w-100 py-2"
                                            onClick={handleSubmit}
                                            disabled={subBtnLoading || !startDate || !endDate}
                                        >
                                            {subBtnLoading ? (
                                                <Spinner size="sm" animation="border" />
                                                ) : (
                                                "Generate Report"
                                            )}
                                        </Button>
                                    </Col>
                                </Row>
                            </div>

                            {/* Report Section */}
                            {itemWiseData.length > 0 && (
                                <div className={`report-section ${skin === "dark" ? "text-light" : ""}`}>
                                    <Card className="mb-4 border-0 shadow-sm">
                                        <Card.Header className="bg-primary text-white">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <h5 className="mb-0">
                                                    Item Wise Sales Summary {dateRangeLabel && `(${dateRangeLabel})`}
                                                </h5>
                                                <small className="text-white-50">
                                                    {itemWiseData.length} items
                                                </small>
                                            </div>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="mb-4">
                                                <Row>
                                                    <Col md={6}>
                                                        <div className="d-flex justify-content-between mb-2">
                                                            <span className="fw-semibold">Outlet:</span>
                                                            <span>{selectedOutlet ? outletName : 'All Outlets'}</span>
                                                        </div>
                                                        <div className="d-flex justify-content-between mb-2">
                                                            <span className="fw-semibold">Date Range:</span>
                                                            <span>{dateRangeLabel}</span>
                                                        </div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <div className="d-flex justify-content-between mb-2">
                                                            <span className="fw-semibold">Total Items Sold:</span>
                                                            <span>{calculateTotals().totalItems}</span>
                                                        </div>
                                                        <div className="d-flex justify-content-between mb-2">
                                                            <span className="fw-semibold">Total Quantity Sold:</span>
                                                            <span>{calculateTotals().totalQty}</span>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>

                                            <div className="table-responsive">
                                                <Table bordered hover className="mb-0">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>#</th>
                                                            <th>Product</th>
                                                            <th>Category</th>
                                                            <th>Type</th>
                                                            <th className="text-end">Qty</th>
                                                            <th className="text-end">Rate</th>
                                                            <th className="text-end">Taxable</th>
                                                            <th className="text-end">Discount</th>
                                                            <th className="text-end">Tax</th>
                                                            <th className="text-end">Total</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {itemWiseData.map((item) => (
                                                            <tr key={item._id}>
                                                                <td>{item.serialNumber}</td>
                                                                <td className="fw-semibold">{item.product}</td>
                                                                <td>{item.category}</td>
                                                                <td>{getFoodTypeBadge(item.foodType)}</td>
                                                                <td className="text-end">{item.quantity}</td>
                                                                <td className="text-end">{formatCurrency(item.rate)}</td>
                                                                <td className="text-end">{formatCurrency(item.totalTaxable)}</td>
                                                                <td className="text-end text-danger">-{formatCurrency(item.totalDiscountAmount)}</td>
                                                                <td className="text-end">{formatCurrency(item.totalTaxAmount)}</td>
                                                                <td className="text-end fw-bold text-success">{formatCurrency(item.grandTotal)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    <tfoot className="table-active">
                                                        <tr>
                                                            <td colSpan="4" className="fw-bold text-end">Totals:</td>
                                                            <td className="text-end fw-bold">{calculateTotals().totalQty}</td>
                                                            <td className="text-end fw-bold">
                                                                {calculateTotals().totalQty > 0 ? 
                                                                    formatCurrency(calculateTotals().actualTaxable / calculateTotals().totalQty) : 
                                                                    formatCurrency(0)}
                                                            </td>
                                                            <td className="text-end fw-bold">
                                                                {formatCurrency(calculateTotals().totalTaxable)}
                                                            </td>
                                                            <td className="text-end fw-bold text-danger">
                                                                -{formatCurrency(calculateTotals().totalDiscount)}
                                                            </td>
                                                            <td className="text-end fw-bold">
                                                                {formatCurrency(calculateTotals().totalTax)}
                                                            </td>
                                                            <td className="text-end fw-bold text-success">
                                                                {formatCurrency(calculateTotals().grandTotal)}
                                                            </td>
                                                        </tr>
                                                    </tfoot>
                                                </Table>
                                            </div>

                                            <div className="mt-4">
                                                <Row>
                                                    <Col md={6}>
                                                        <Card className="border-0 shadow-sm">
                                                            <Card.Header className="bg-light">
                                                                <h6 className="mb-0 fw-semibold">Category Performance</h6>
                                                            </Card.Header>
                                                            <Card.Body>
                                                                <Table bordered className="mb-0">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Category</th>
                                                                            <th className="text-end">Items</th>
                                                                            <th className="text-end">Qty Sold</th>
                                                                            <th className="text-end">Revenue</th>
                                                                            <th className="text-end">%</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {getCategoryStats().map((category) => (
                                                                            <tr key={category.name}>
                                                                                <td>{category.name}</td>
                                                                                <td className="text-end">{category.count}</td>
                                                                                <td className="text-end">{category.quantity}</td>
                                                                                <td className="text-end">{formatCurrency(category.total)}</td>
                                                                                <td className="text-end">{category.percentage.toFixed(1)}%</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </Table>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                    <Col md={6}>
                                                        <Card className="border-0 shadow-sm">
                                                            <Card.Header className="bg-light">
                                                                <h6 className="mb-0 fw-semibold">Food Type Performance</h6>
                                                            </Card.Header>
                                                            <Card.Body>
                                                                <Table bordered className="mb-0">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Type</th>
                                                                            <th className="text-end">Items</th>
                                                                            <th className="text-end">Qty Sold</th>
                                                                            <th className="text-end">Revenue</th>
                                                                            <th className="text-end">%</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {getFoodTypeStats().map((type) => (
                                                                            <tr key={type.name}>
                                                                                <td>{getFoodTypeBadge(type.name)}</td>
                                                                                <td className="text-end">{type.count}</td>
                                                                                <td className="text-end">{type.quantity}</td>
                                                                                <td className="text-end">{formatCurrency(type.total)}</td>
                                                                                <td className="text-end">{type.percentage.toFixed(1)}%</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </Table>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                </Row>
                                            </div>
                                        </Card.Body>
                                        <Card.Footer className="text-muted">
                                            <small>Summary generated on {format(new Date(), 'dd MMMM yyyy hh:mm a')}</small>
                                        </Card.Footer>
                                    </Card>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    )
}

export default ItemWiseSummary